import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaPlus, FaTimes } from "react-icons/fa";
import { Drawer, EmptyState, Modal } from "../shared";
import { TextInput, Textarea } from "../atoms";
import {
  useGetTaskProjectsQuery,
  useCreateTaskProjectMutation,
  useUpdateTaskProjectMutation,
  useArchiveTaskProjectMutation,
  useSetTaskProjectMembersMutation,
} from "../../store";
import useAuth from "../../Hooks/useAuth";
import usePermission from "../../Hooks/usePermission";
import { useAlert } from "../../context/AlertContext";
import ColorPalette from "./atoms/ColorPalette";
import ProjectMembersEditor from "./ProjectMembersEditor";
import LabelManager from "./LabelManager";
import TaskConfirmModal from "./TaskConfirmModal";
import { NestedOverlayProvider } from "./nestedOverlay";
import { colorClass } from "./taskConstants";

export const KEY_RE = /^[A-Z]{2,6}$/;
export const KEY_MESSAGE = "Proje kodu 2-6 büyük harften oluşmalı.";
export const VISIBILITIES = [
  { id: "open", label: "Açık", help: "İş Planlama'ya erişimi olan herkes projedeki tüm işleri görür." },
  { id: "restricted", label: "Kısıtlı", help: "Yalnız proje üyeleri projedeki tüm işleri görür." },
];

const errText = (e, fallback = "İşlem başarısız.") => e?.data?.message || e?.message || fallback;
const btn = "px-3 py-1.5 rounded-lg text-sm font-Medium disabled:opacity-50";
const primaryBtn = `${btn} bg-main text-white hover:opacity-90`;

function FieldLabel({ children }) {
  return <p className="block text-sm font-SemiBold text-gray-700 mb-1.5">{children}</p>;
}

function VisibilityRadio({ value, onChange }) {
  return (
    <div role="radiogroup" aria-label="Görünürlük" className="space-y-2">
      {VISIBILITIES.map((v) => (
        <label
          key={v.id}
          className={`flex items-start gap-3 rounded-xl border px-3 py-2 cursor-pointer ${
            value === v.id ? "border-main bg-gray-50" : "border-gray-200"
          }`}
        >
          <input
            type="radio"
            name="task-project-visibility"
            className="mt-1"
            checked={value === v.id}
            onChange={() => onChange(v.id)}
          />
          <span>
            <span className="block text-sm font-Medium text-gray-800">{v.label}</span>
            <span className="block text-xs text-gray-500">{v.help}</span>
          </span>
        </label>
      ))}
    </div>
  );
}

function ProjectBadge({ project }) {
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-Medium ${colorClass(project.color)}`}>
      {project.key}
    </span>
  );
}

// Yeni proje (tasks.admin)
function NewProjectModal({ open, onClose }) {
  const { auth } = useAuth();
  const { showError, showSuccess } = useAlert();
  const [createProject, { isLoading }] = useCreateTaskProjectMutation();
  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("blue");
  const [visibility, setVisibility] = useState("open");
  const [error, setError] = useState("");

  const keyValid = KEY_RE.test(key);
  const canSave = keyValid && name.trim().length > 0 && !isLoading;

  const submit = async () => {
    if (!canSave) return;
    setError("");
    try {
      await createProject({
        token: auth?.accessToken,
        key,
        name: name.trim(),
        description,
        color,
        visibility,
      }).unwrap();
      showSuccess("Proje oluşturuldu.");
      setKey("");
      setName("");
      setDescription("");
      setColor("blue");
      setVisibility("open");
      onClose();
    } catch (e) {
      const msg = errText(e, "Proje oluşturulamadı.");
      setError(msg);
      showError(msg);
    }
  };

  return (
    <Modal open={open} onClose={isLoading ? undefined : onClose} maxWidth="lg">
      <Modal.Header title="Yeni Proje" onClose={isLoading ? undefined : onClose} />
      <Modal.Body className="max-h-[70vh] overflow-y-auto space-y-4">
        <TextInput
          label="Kod"
          required
          value={key}
          maxLength={6}
          placeholder="FAB"
          onChange={(e) => setKey(e.target.value.toLocaleUpperCase("en-US"))}
          error={key.length > 0 && !keyValid}
          errorMessage={KEY_MESSAGE}
        />
        <TextInput label="Ad" required value={name} maxLength={80} placeholder="Proje adı" onChange={(e) => setName(e.target.value)} />
        <Textarea label="Açıklama" value={description} onChange={(e) => setDescription(e.target.value)} />
        <div>
          <FieldLabel>Renk</FieldLabel>
          <ColorPalette value={color} onChange={setColor} />
        </div>
        <div>
          <FieldLabel>Görünürlük</FieldLabel>
          <VisibilityRadio value={visibility} onChange={setVisibility} />
        </div>
        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}
      </Modal.Body>
      <Modal.Footer className="flex justify-end gap-2">
        <button type="button" onClick={onClose} disabled={isLoading} className={`${btn} text-gray-600 hover:bg-gray-100`}>
          Vazgeç
        </button>
        <button type="button" onClick={submit} disabled={!canSave} className={primaryBtn}>
          {isLoading ? "Kaydediliyor..." : "Kaydet"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

// Proje düzenleme çekmecesinin içeriği (proje değişince key ile sıfırlanır)
function ProjectEditor({ project, onClose, onRequestClose, dirtyRef }) {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { showError, showSuccess } = useAlert();
  const [updateProject, { isLoading: saving }] = useUpdateTaskProjectMutation();
  const [archiveProject, { isLoading: archiving }] = useArchiveTaskProjectMutation();
  const [setMembers, { isLoading: savingMembers }] = useSetTaskProjectMembersMutation();

  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description || "");
  const [color, setColor] = useState(project.color);
  const [visibility, setVisibility] = useState(project.visibility);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [confirmDirtyArchive, setConfirmDirtyArchive] = useState(false);
  const [membersDirty, setMembersDirty] = useState(false);

  const archived = Boolean(project.archivedAt);
  const dirty =
    name.trim() !== project.name ||
    description !== (project.description || "") ||
    color !== project.color ||
    visibility !== project.visibility;

  const anyDirty = dirty || membersDirty;
  dirtyRef.current = anyDirty;
  useEffect(() => () => {
    dirtyRef.current = false;
  }, [dirtyRef]);

  const run = async (fn, okMsg) => {
    try {
      await fn().unwrap();
      if (okMsg) showSuccess(okMsg);
      return true;
    } catch (e) {
      showError(errText(e));
      return false;
    }
  };

  const save = () =>
    run(
      () => updateProject({ token, id: project._id, name: name.trim(), description, color, visibility }),
      "Proje güncellendi."
    );

  const saveMembers = (members) =>
    run(
      () => setMembers({ token, id: project._id, members: members.map((m) => ({ user: m.user._id, role: m.role })) }),
      "Üyeler güncellendi."
    );

  const toggleArchive = async () => {
    const ok = await run(
      () => archiveProject({ token, id: project._id, archived: !archived }),
      archived ? "Proje arşivden çıkarıldı." : "Proje arşivlendi."
    );
    if (ok) {
      setConfirmArchive(false);
      onClose();
    }
  };

  return (
    <>
      <Drawer.Header className="px-4 sm:px-5 py-3 bg-slate-50/80 border-b border-slate-200">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <ProjectBadge project={project} />
            <span className="truncate text-base font-SemiBold text-slate-800">{project.name}</span>
            {archived && (
              <span className="px-2 py-0.5 rounded-md text-xs font-Medium bg-gray-100 text-gray-600">Arşivli</span>
            )}
          </div>
          <button
            type="button"
            onClick={onRequestClose}
            aria-label="Kapat"
            className="w-9 h-9 rounded-xl flex items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-slate-200"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>
      </Drawer.Header>
      <Drawer.Body className="p-4 sm:p-5 space-y-6">
        {archived && (
          <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-700" data-testid="archived-note">
            Arşivli proje — düzenlemek için arşivden çıkarın
          </p>
        )}
        <fieldset disabled={archived} className="space-y-4 min-w-0 border-0 p-0 m-0">
          <TextInput label="Ad" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
          <Textarea label="Açıklama" value={description} onChange={(e) => setDescription(e.target.value)} />
          <div>
            <FieldLabel>Renk</FieldLabel>
            <ColorPalette value={color} onChange={setColor} />
          </div>
          <div>
            <FieldLabel>Görünürlük</FieldLabel>
            <VisibilityRadio value={visibility} onChange={setVisibility} />
          </div>
          <div className="flex justify-end">
            <button type="button" disabled={!dirty || !name.trim() || saving} onClick={save} className={primaryBtn}>
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </fieldset>

        <section>
          <h4 className="text-sm font-SemiBold text-gray-800 mb-2">Üyeler</h4>
          <ProjectMembersEditor
            key={(project.members || []).map((m) => `${m.user._id}:${m.role}`).join("|")}
            members={project.members || []}
            saving={savingMembers}
            disabled={archived}
            onDirtyChange={setMembersDirty}
            onSave={saveMembers}
          />
        </section>

        <section className="pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={() => (anyDirty ? setConfirmDirtyArchive(true) : setConfirmArchive(true))}
            className={`${btn} border border-gray-200 text-gray-700 hover:bg-gray-50`}
          >
            {archived ? "Arşivden Çıkar" : "Arşivle"}
          </button>
        </section>
      </Drawer.Body>
      <TaskConfirmModal
        open={confirmDirtyArchive}
        title="Kaydedilmemiş değişiklikler"
        message={`Kaydedilmemiş değişiklikler var. Yine de ${archived ? "arşivden çıkarılsın" : "arşivlensin"} mi?`}
        confirmLabel={archived ? "Arşivden Çıkar" : "Arşivle"}
        onConfirm={() => {
          setConfirmDirtyArchive(false);
          setConfirmArchive(true);
        }}
        onClose={() => setConfirmDirtyArchive(false)}
      />
      <TaskConfirmModal
        open={confirmArchive}
        title={archived ? "Projeyi arşivden çıkar" : "Projeyi arşivle"}
        message={
          archived
            ? `${project.key} yeniden iş açmaya ve düzenlemeye açılacak. Emin misiniz?`
            : `${project.key} arşivlenecek; içinde iş açılamaz ve işler düzenlenemez, yalnız görüntülenir. Emin misiniz?`
        }
        confirmLabel={archived ? "Arşivden Çıkar" : "Arşivle"}
        busyLabel="Kaydediliyor..."
        busy={archiving}
        onConfirm={toggleArchive}
        onClose={() => setConfirmArchive(false)}
      />
    </>
  );
}

// İş Planlama ayarları: projeler (tasks.admin: tümü + yeni; yönetici: yalnız yönettikleri) ve etiketler (tasks.admin)
function TaskSettings() {
  const { auth } = useAuth();
  const { hasPermission } = usePermission();
  const isAdmin = hasPermission("tasks.admin");
  const { data, isLoading } = useGetTaskProjectsQuery({ token: auth?.accessToken, includeArchived: true });
  const [newOpen, setNewOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const overlaysRef = useRef(0);
  const dirtyRef = useRef(false);
  const [confirmClose, setConfirmClose] = useState(false);

  const projects = useMemo(() => {
    const all = data?.data?.projects || [];
    return isAdmin ? all : all.filter((p) => p?.can?.manage);
  }, [data, isAdmin]);
  const selected = projects.find((p) => p._id === selectedId) || null;

  const closeDrawer = () => {
    if (overlaysRef.current > 0) return;
    if (dirtyRef.current) {
      setConfirmClose(true);
      return;
    }
    setSelectedId(null);
  };

  if (!isAdmin && !isLoading && projects.length === 0) {
    return <EmptyState message="Yönetebileceğiniz bir proje yok" />;
  }

  return (
    <div className="p-4 sm:p-6 space-y-8 overflow-y-auto" data-testid="task-settings">
      <section data-testid="projects-section">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h3 className="text-base font-SemiBold text-gray-800">Projeler</h3>
          {isAdmin && (
            <button type="button" onClick={() => setNewOpen(true)} className={`${primaryBtn} inline-flex items-center gap-2`}>
              <FaPlus className="w-3 h-3" aria-hidden="true" />
              Yeni Proje
            </button>
          )}
        </div>

        {projects.length === 0 ? (
          <EmptyState message="Henüz proje yok" />
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs text-gray-500">
                <tr>
                  <th className="px-3 py-2 font-Medium">Kod</th>
                  <th className="px-3 py-2 font-Medium">Ad</th>
                  <th className="px-3 py-2 font-Medium">Görünürlük</th>
                  <th className="px-3 py-2 font-Medium">Üye</th>
                  <th className="px-3 py-2 font-Medium">Durum</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {projects.map((p) => (
                  <tr
                    key={p._id}
                    data-testid="project-row"
                    onClick={() => setSelectedId(p._id)}
                    className="cursor-pointer hover:bg-gray-50"
                  >
                    <td className="px-3 py-2">
                      <ProjectBadge project={p} />
                    </td>
                    <td className="px-3 py-2 text-gray-800">{p.name}</td>
                    <td className="px-3 py-2 text-gray-600">{p.visibility === "restricted" ? "Kısıtlı" : "Açık"}</td>
                    <td className="px-3 py-2 text-gray-600">{(p.members || []).length}</td>
                    <td className="px-3 py-2">
                      {p.archivedAt && (
                        <span className="px-2 py-0.5 rounded-md text-xs font-Medium bg-gray-100 text-gray-600">
                          Arşivli
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {isAdmin && <LabelManager />}

      {isAdmin && <NewProjectModal open={newOpen} onClose={() => setNewOpen(false)} />}

      <Drawer open={Boolean(selected)} onClose={closeDrawer} width="lg">
        <NestedOverlayProvider counterRef={overlaysRef}>
          {selected && <ProjectEditor key={selected._id} project={selected} onRequestClose={closeDrawer} dirtyRef={dirtyRef}
              onClose={() => {
                dirtyRef.current = false;
                setSelectedId(null);
              }}
            />}
          <TaskConfirmModal
            open={confirmClose}
            title="Kaydedilmemiş değişiklikler"
            message="Kaydedilmemiş değişiklikler var. Kapatılsın mı?"
            confirmLabel="Kapat"
            onConfirm={() => {
              setConfirmClose(false);
              dirtyRef.current = false;
              setSelectedId(null);
            }}
            onClose={() => setConfirmClose(false)}
          />
        </NestedOverlayProvider>
      </Drawer>
    </div>
  );
}

export default TaskSettings;
